import React, { useState, useEffect, useRef } from 'react';
import { FiX } from 'react-icons/fi';
import { PlantInfo } from '../types/api';
import '../styles/components/PlantChat.css';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/api';
import { formatPlantDisplayName } from '../utils/dataConversion';

export interface Message {
  id: string;
  sender: 'user' | 'bot';
  text: string;
}

interface PlantChatProps {
  plantData: PlantInfo;
  messages: Message[];
  onNewMessage: (message: Message) => void;
  userRole?: string;
}

const INVASIVE_QUESTIONS = [
  "What is the threat level?",
  "What are the harms of this species?",
  "What are the native lookalikes?",
  "What are the native replacements?",
  "What is the best course of action?",
  "Tell me more about this plant"
];

const NATIVE_QUESTIONS = [
  "What are the benefits of this plant?"
];

const PlantChat: React.FC<PlantChatProps> = ({ plantData, messages, onNewMessage, userRole = 'Student' }) => {
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { firebaseUser } = useAuth();
  const initializedRef = useRef(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const IconX = FiX as any;

  // Initialize with welcome message
  useEffect(() => {
    if (messages.length === 0 && !initializedRef.current) {
      initializedRef.current = true;
      const displayName = formatPlantDisplayName(plantData.scientificName, plantData.commonName);

      onNewMessage({
        id: 'welcome',
        sender: 'bot',
        text: `Hello! I'm your plant expert. I can tell you more about ${displayName}. What would you like to know?`
      });
    }
  }, [messages.length, plantData, onNewMessage]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleQuestionClick = async (question: string) => {
    if (loading) return;

    onNewMessage({
      id: Date.now().toString(),
      sender: 'user',
      text: question
    });
    setLoading(true);

    try {
      const context = {
        species: formatPlantDisplayName(plantData.scientificName, plantData.commonName),
        region: plantData.region,
        invasiveOrNot: plantData.isInvasive,
        description: plantData.description,
        invasiveEffects: plantData.impact,
        userRole: userRole
      };

      const token = firebaseUser ? await firebaseUser.getIdToken() : null;
      
      const response = await fetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ message: question, context: context })
      });

      if (!response.ok) throw new Error('Failed to get response');

      const data = await response.json();
      onNewMessage({
        id: (Date.now() + 1).toString(),
        sender: 'bot',
        text: data.text
      });
    } catch (error) {
      console.error('Chat error:', error);
      onNewMessage({
        id: (Date.now() + 1).toString(),
        sender: 'bot',
        text: "I'm sorry, I'm having trouble connecting right now."
      });
    } finally {
      setLoading(false);
    }
  };

  const questions = plantData.isInvasive ? INVASIVE_QUESTIONS : NATIVE_QUESTIONS;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="plant-chat-container">
      <div className="chat-messages">
        {messages.map((msg) => (
          <div key={msg.id} className={`message ${msg.sender}`}>
            <div className="message-bubble">{msg.text}</div>
          </div>
        ))}
        {loading && (
          <div className="message bot">
            <div className="message-bubble loading">
              <span className="dot"></span><span className="dot"></span><span className="dot"></span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-area">
        <button className="question-popup-trigger" onClick={() => setIsDropdownOpen(true)} disabled={loading}>
          ASK A QUESTION
        </button>
      </div>

      {isDropdownOpen && (
        <div className="question-popup-container" ref={dropdownRef}>
          <div className="question-popup">
            <div className="popup-header">
              <h3 className="popup-title">Select a question to ask:</h3>
              <button className="popup-close" onClick={() => setIsDropdownOpen(false)} type="button">
                <IconX />
              </button>
            </div>
            <div className="popup-content">
              {questions.map((question, index) => (
                <button
                  key={index}
                  className="popup-question-item"
                  onClick={() => {
                    handleQuestionClick(question);
                    setIsDropdownOpen(false);
                  }}
                  disabled={loading}
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlantChat;